<template>
  <div
    class="condition-group"
    :class="{ 'condition-group--root': isRootGroup, 'condition-group--nested': !isRootGroup }"
  >
    <div class="condition-group__heading">
      <span>
        <template v-if="group.rules.length === 1 && group.subGroups.length === 0">
          {{ t('conditions.when_statement_true') }}
        </template>
        <template v-else>
          {{ t('conditions.when') }}
          {{ group.logic === 'AND' ? t('conditions.all') : t('conditions.any') }}
          {{ t('conditions.of_following_true') }}
        </template>
      </span>
      <Btn v-if="canRemove" icon="minus" variant="tertiary" @click="removeGroup" />
    </div>
    <div v-for="(rule, ruleIdx) in group.rules" :key="ruleIdx" class="condition-group-rule">
      <div class="condition-group-rule__before">
        <span v-if="ruleIdx === 0">{{ t('conditions.where') }}</span>
        <template v-else-if="ruleIdx === 1">
          <span class="logic-selector-wrapper">
            <select v-model="group.logic" class="logic-selector">
              <option value="AND">{{ t('conditions.all') }}</option>
              <option value="OR">{{ t('conditions.any') }}</option>
            </select>
            <span class="logic-chevron"><BaseIcon name="chevron" size="12" /></span>
          </span>
        </template>
        <span v-else>{{ group.logic === 'AND' ? t('conditions.all') : t('conditions.any') }}</span>
      </div>
      <div class="condition-group-rule__body">
        <div class="form-group form-group--has-success">
          <div class="sf-input sf-input--select">
            <select v-model="rule.attribute">
              <option :value="null">{{ t('conditions.select_field') }}</option>
              <option v-for="attr in attributes" :key="attr.key" :value="attr.key">
                {{ attr.label }}
              </option>
            </select>
          </div>
        </div>
        <div class="form-group form-group--has-success">
          <div class="sf-input sf-input--select">
            <select v-model="rule.operator">
              <option value="==">{{ t('conditions.equals') }}</option>
              <option value="!=">{{ t('conditions.not_equals') }}</option>
              <option value=">">{{ t('conditions.greater_than') }}</option>
              <option value=">=">{{ t('conditions.greater_or_equal') }}</option>
              <option value="<">{{ t('conditions.less_than') }}</option>
              <option value="<=">{{ t('conditions.less_or_equal') }}</option>
            </select>
          </div>
        </div>
        <div class="form-group form-group--has-success condition-group-rule__input">
          <div class="sf-input sf-input--select">
            <template v-if="getSelectedAttr(rule)">
              <template
                v-if="getSelectedAttr(rule).type === 'select' && getSelectedAttr(rule).options"
              >
                <select v-model="rule.value">
                  <option :value="null">{{ t('conditions.select_value') }}</option>
                  <option
                    v-for="opt in getSelectedAttr(rule).options"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </template>
              <template v-else-if="getSelectedAttr(rule).type === 'number'">
                <input
                  v-model.number="rule.value"
                  type="number"
                  :placeholder="t('conditions.enter_number')"
                />
              </template>
              <template v-else-if="getSelectedAttr(rule).type === 'boolean'">
                <select v-model="rule.value">
                  <option :value="true">{{ t('conditions.true') }}</option>
                  <option :value="false">{{ t('conditions.false') }}</option>
                </select>
              </template>
              <template v-else>
                <input v-model="rule.value" :placeholder="t('conditions.enter_value')" />
              </template>
            </template>
          </div>
        </div>
      </div>
      <div class="condition-group-rule__after">
        <Btn
          v-if="group.rules.length > 1"
          icon="minus"
          variant="dashed"
          @click="removeRule(ruleIdx)"
        />
      </div>
    </div>
    <!-- Subgroepen -->
    <div v-for="(sub, subIdx) in group.subGroups" :key="subIdx" class="condition-group__subgroup">
      <CPQConditionGroup
        :group="sub"
        :attributes="attributes"
        :canRemove="true"
        :isRoot="false"
        @remove-group="removeSubGroup(subIdx)"
      />
    </div>
    <div class="condition-group__actions">
      <Btn @click.prevent="addRule" icon="plus-circle" variant="dashed">
        {{ t('conditions.add_filter') }}
      </Btn>
      <Btn @click.prevent="addSubGroup" icon="plus-circle" variant="dashed">
        {{ t('conditions.add_group') }}
      </Btn>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import Btn from '@/components/partials/Btn.vue';
  import CPQConditionGroup from './CPQConditionGroup.vue';
  import BaseIcon from '@/components/partials/BaseIcon.vue';

  const { t } = useI18n();

  const props = defineProps<{
    group: any;
    attributes: any[];
    canRemove?: boolean;
    isRoot?: boolean;
  }>();

  // Default isRoot to true if not provided
  const isRootGroup = props.isRoot !== false;
  const emit = defineEmits(['remove-group']);

  function getSelectedAttr(rule: any) {
    return props.attributes.find((a) => a.key === rule.attribute);
  }

  function addRule() {
    props.group.rules.push({ attribute: null, operator: '==', value: '' });
  }
  function removeRule(idx: number) {
    props.group.rules.splice(idx, 1);
  }
  function addSubGroup() {
    props.group.subGroups.push({
      logic: 'AND',
      rules: [{ attribute: null, operator: '==', value: '' }],
      subGroups: [],
    });
  }
  function removeSubGroup(idx: number) {
    props.group.subGroups.splice(idx, 1);
  }
  function removeGroup() {
    emit('remove-group');
  }
</script>

<style lang="scss" scoped>
  .condition-group {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    // Root group - transparent, no border (wrapper provides styling)
    &--root {
      background-color: transparent;
      border: none;
      padding: 0;
    }

    // Nested groups - distinct styling
    &--nested {
      background-color: white;
      border-radius: 0.5rem;
      border: 1px solid var(--slate-200);
      padding: 0.75rem;
      margin-top: 0.5rem;
    }

    &__heading {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      color: var(--slate-500);
      font-size: 0.875rem;

      .icon {
        color: var(--slate-400);
        @include hover {
          color: var(--slate-700);
        }
      }
    }

    &__subgroup {
      margin-left: 1.5rem;
      padding-left: 0.75rem;
      border-left: 2px solid var(--slate-200);
    }

    .condition-group-rule {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      gap: 1rem;
      .logic-selector-wrapper {
        position: relative;
        display: inline-block;
        vertical-align: middle;
      }
      .logic-selector {
        border: 1px solid var(--brand-200);
        border-radius: 999px;
        background: var(--brand-100);
        color: var(--brand-700);
        font-weight: 600;
        font-size: 0.95rem;
        padding: 0.15rem 1.75rem 0.15rem 0.75rem;
        outline: none;
        appearance: none;
        transition: border 0.2s;
        width: 100%;
        &:focus {
          border-color: var(--brand-500);
        }
      }
      .logic-chevron {
        pointer-events: none;
        position: absolute;
        right: 0.9rem;
        top: 50%;
        transform: rotate(180deg) translateY(50%);
        font-size: 0.8em;
        color: var(--brand-700);
      }
      &__before {
        width: 5rem;
        color: var(--slate-700);
        font-weight: 500;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        padding-right: 0.5rem;
      }
      .form-group {
        padding: 0;
      }
      &__body {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex: 1;
      }
      &__after {
        display: flex;
        align-items: center;
        margin-left: 0.5rem;
      }
      &__before {
        width: 5rem;
        color: var(--slate-500);
      }
      &__body {
        display: flex;
        flex-grow: 1;
        gap: 0.25rem;
        .form-group {
          padding: 0;
        }
      }
      &__after {
        .icon {
          color: var(--slate-400);
          @include hover {
            color: var(--slate-700);
          }
        }
      }
      :deep(.logic-selector) {
        select {
          padding: 0.25rem 0.75rem 0.25rem 0;
          background-color: transparent !important;
          color: var(--slate-800);
          font-weight: 500;
          border-color: transparent;
        }
      }
    }
    &__actions {
      margin-top: 1rem;
      display: flex;
      gap: 0.5rem;
    }
  }
</style>
